import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import router from '@/router'
import AuthService, { User, LoginCredentials } from '@/services/auth'
import { useToastService } from '@/services/toast'

export const useAuthStore = defineStore('auth', () => {
  // Toast service
  const toast = useToastService()
  
  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(
    localStorage.getItem('token')
  )
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const currentUser = computed(() => user.value)
  const isLoading = computed(() => loading.value)
  const hasError = computed(() => !!error.value)
  const isAdmin = computed(() => user.value?.user_type === 0) // Admin user_type is 0

  // Actions
  async function login(credentials: LoginCredentials) {
    loading.value = true
    error.value = null

    try {
      const response = await AuthService.login({
        ...credentials,
        remember_me: credentials.remember_me ?? false
      })

      if (response.success && response.data.user) {
        user.value = response.data.user
        
        // Use the actual Sanctum token from the API response
        const authToken = response.data.token
        if (authToken) {
          token.value = authToken

          // The remember_me flag only affects backend token expiration time:
          // - remember_me true: 30 days
          // - remember_me false: 24 hours
          localStorage.setItem('token', authToken)
          sessionStorage.removeItem('token')  // Clear any session token
          
          // Show success toast notification
          toast.success('You have been logged in successfully.')
          
          // Determine target path based on user type or query redirect
          const redirectPath = router.currentRoute.value.query.redirect as string
          let targetPath = redirectPath || '/'
          
          // Resolve '/' to the correct dashboard based on user type
          if (targetPath === '/') {
            const userType = response.data.user.user_type_label || response.data.user.user_type
            if (userType === 'Admin' || userType === 0) {
              targetPath = '/employee'
            } else {
              targetPath = '/dashboard'
            }
          }
          
          // Detect if running inside an iframe (e.g., embedded on WordPress)
          const isInIframe = window.self !== window.top
          
          if (isInIframe) {
            // Build the full portal URL using the configured base URL and open in a new tab
            const baseUrl = process.env.VUE_APP_BASE_URL || ''
            const portalUrl = window.location.origin + baseUrl + targetPath
            window.open(portalUrl, '_blank')
          } else {
            // Normal navigation within the same window
            await router.push(targetPath)
          }
        } else {
          throw new Error('No authentication token received from server')
        }
      } else {
        throw new Error(response.message || 'Login failed')
      }
    } catch (err: any) {
      console.error('Login error:', err)
      
      let errorMessage = 'Login failed. Please try again.'
      
      if (err.success === false) {
        errorMessage = err.message || errorMessage
      } else if (err.errors) {
        // Handle validation errors
        const firstError = Object.values(err.errors)[0] as string[]
        errorMessage = firstError[0] || errorMessage
      } else if (err.message) {
        errorMessage = err.message
      }
      
      error.value = errorMessage
      
      /* 
        -- Optionally, you can disable toast notifications for specific errors
        -- Uncomment the following lines if you want to skip notifications for specific errors 
      */

      // // Don't show toast notifications for invalid credentials or inactive account errors
      // const skipNotificationErrors = [
      //   'Invalid credentials provided.',
      //   'Your account is inactive. Please contact administrator.'
      // ]
      // if (!skipNotificationErrors.includes(errorMessage)) {
      //   // Show error toast for other login failures
      //   toast.error('Login Failed', errorMessage)
      // }

      toast.error(errorMessage)
      
      throw err
    } finally {
      loading.value = false
    }
  }

  async function logout(showMessage = true) {
    loading.value = true
    error.value = null

    try {
      // Call logout API
      await AuthService.logout()
      
      if (showMessage) {
        toast.success('You have been successfully logged out.')
      }
    } catch (err: any) {
      console.warn('Logout API call failed:', err)
      // Continue with local logout even if API fails
    } finally {
      // Clear local state regardless of API call result
      token.value = null
      user.value = null
      localStorage.removeItem('token')
      
      // Redirect to login
      await router.push('/login')
      loading.value = false
    }
  }

  async function checkAuth(): Promise<boolean> {
    if (!token.value) {
      return false
    }

    // If user profile is already in memory, avoid redundant network calls on route transitions
    if (user.value) {
      return true
    }

    loading.value = true
    error.value = null

    try {
      const response = await AuthService.getProfile()
      
      if (response.success && response.data.user) {
        user.value = response.data.user
        return true
      } else {
        throw new Error('Invalid response from server')
      }
    } catch (err: any) {
      console.warn('Auth check failed:', err)
      
      const status = err?.status || err?.response?.status
      // Only clear token and reset session on explicit 401 Unauthorized or 403 Forbidden
      if (status === 401 || status === 403) {
        token.value = null
        user.value = null
        localStorage.removeItem('token')
        return false
      }
      
      // For temporary server/network errors, preserve token if user is already populated
      if (user.value) {
        return true
      }

      return false
    } finally {
      loading.value = false
    }
  }

  async function refreshProfile() {
    if (!token.value) return

    try {
      const response = await AuthService.getProfile()
      if (response.success && response.data.user) {
        user.value = response.data.user
      }
    } catch (err) {
      console.warn('Failed to refresh profile:', err)
    }
  }

  // Clear any errors
  function clearError() {
    error.value = null
  }

  return {
    // State
    user,
    token,
    loading,
    error,
    // Getters
    isAuthenticated,
    currentUser,
    isLoading,
    hasError,
    isAdmin,
    // Actions
    login,
    logout,
    checkAuth,
    refreshProfile,
    clearError
  }
}) 